import { useSelector } from 'react-redux';
import { PERMISSION } from '../../components/investigation/share/user_roles';
import { FUNCTIONALITY } from '../../constants/types';
import { useCurrentInvestigation } from '../../hooks';
import { getPersonalDataHistoryService } from '../../services';
import { decryptPersonalValue, formatBirthdateForDisplay, getInvestigationRawKey } from '../../utils';
import { getPersonalDataKek } from '../../utils/personalDataV2';
import { AmendmentHistoryView, useAmendmentHistory } from './useAmendmentHistory';
import { DecryptPersonalValue, PersonalDataHistoryRow, PersonalFieldView, toPersonalDataEntries } from './personalDataHistory';
import { isCorrectionOnlySession } from './MyCorrections/correctionAccess';

type Translate = (id: string) => unknown;

/** Decrypts with the same keys and rule as the patient list (decryptPersonalValue); each key loads once. */
function personalValueDecryptor(investigation: any): DecryptPersonalValue {
    let keys: Promise<{ kek: CryptoKey | null; legacyKey: string | null }> | null = null;
    return async (ciphertext, nonce) => {
        keys ??= getPersonalDataKek(investigation.uuid).catch(() => null).then((kek) => ({
            kek,
            legacyKey: getInvestigationRawKey(investigation.uuid, investigation.encryptedKeyUsed, investigation.keyResearcherInvestigation),
        }));
        return decryptPersonalValue(ciphertext, nonce, await keys);
    };
}

/** A label is a translation id for the standard fields and free text for a study's own. */
function fieldLabel(label: string, translate: Translate): string {
    const text = String(translate(label));
    return text.includes('Missing translationId') ? label : text;
}

function personalFieldViews(investigation: any, translate: Translate, languageCode: string): Record<string, PersonalFieldView> {
    const views: Record<string, PersonalFieldView> = {};
    for (const field of investigation?.personalFields ?? []) {
        views[field.name] = {
            label: fieldLabel(field.label ?? field.name, translate),
            // the patient list's rule, so the history and the page read a field the same way
            encrypted: field.encrypted === true,
            format: field.type === 'date' ? (plain) => formatBirthdateForDisplay(plain, languageCode) || plain : (plain) => plain,
        };
    }
    return views;
}

/**
 * SKIN-374 — a patient's personal-data history, ready for AmendmentHistory.
 * Asked only where the user may read personal data (PERSONAL_ACCESS) and the
 * study has WEB_CLINICAL_CORRECTION, the pair the API checks. Never in a dermatologist's web
 * session (SKIN-600): the API keeps personal data out of it.
 */
export function usePersonalDataHistory(uuidPatient: string, translate: Translate, languageCode: string): AmendmentHistoryView {
    const { currentInvestigation, hasFunctionality } = useCurrentInvestigation();
    const correctionSession = useSelector((state: any) => isCorrectionOnlySession(state.investigations.data));
    const enabled = hasFunctionality(FUNCTIONALITY.WEB_CLINICAL_CORRECTION)
        && !!currentInvestigation?.permissions?.includes(PERMISSION.PERSONAL_ACCESS)
        && !correctionSession;
    return useAmendmentHistory(enabled ? `${currentInvestigation.uuid}/${uuidPatient}/${languageCode}` : null, async () => {
        const data: { history?: PersonalDataHistoryRow[] } = await getPersonalDataHistoryService(currentInvestigation.uuid, uuidPatient);
        return toPersonalDataEntries(
            data.history ?? [],
            personalFieldViews(currentInvestigation, translate, languageCode),
            personalValueDecryptor(currentInvestigation),
        );
    });
}
